/*
Origin Core 1.0

called from /snippets/scripts.liquid

DEVELOPER NOTE
--------------------
any changes to this file should be noted here, and also noted in the following wiki page:
https://unifiedco.atlassian.net/wiki/spaces/ORIGIN/pages/1413414932/Core+File+Changes+1.0

SUMMARY
--------------------
This script defines a custom web component <cart-drawer-component> that manages the Shopify cart drawer UI. 
It extends a dialog component, listens for cart add events to automatically open the drawer, and ensures the drawer closes when certain actions (like clicking the installments CTA) occur. 
The component handles opening and closing the drawer and cleans up event listeners when disconnected. 
It is registered as a custom element if not already defined.

*/

import { DialogComponent } from '@theme/dialog';
import { CartAddEvent, CartUpdateEvent, ThemeEvents } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    // console.log('Cart drawer component connected, listening for events:', {
    //   cartAdd: ThemeEvents.cartAdd,
    //   cartUpdate: ThemeEvents.cartUpdate,
    //   hasAutoOpen: this.hasAttribute('auto-open')
    // });
    
    document.addEventListener(ThemeEvents.cartAdd, this.#handleCartAdd);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartAdd, this.#handleCartAdd);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
  }

  #handleCartAdd = (event) => {
    console.log('Cart drawer received cart add event:', {
      source: event.detail?.data?.source,
      hasAutoOpen: this.hasAttribute('auto-open'),
      sections: event.detail?.data?.sections,
      timestamp: Date.now()
    });
    
    if (this.hasAttribute('auto-open')) {
      console.log('Opening cart drawer from cart add event');
      this.showDialog();
      
      // If we have sections data, update the cart contents
      if (event.detail?.data?.sections) {
        console.log('Cart add event has sections data, updating cart contents');
        this.#updateCartContents(event.detail.data.sections);
      }
    }
  };

  #handleCartUpdate = (event) => {
    // Check if the cart update event has the preventCartDrawer flag
    const { preventCartDrawer } = event.detail?.data || {};
    
    // console.log('Cart drawer received cart update event:', {
    //   preventCartDrawer,
    //   source: event.detail?.data?.source,
    //   hasAutoOpen: this.hasAttribute('auto-open'),
    //   timestamp: Date.now()
    // });
    
    // Only open the cart drawer if preventCartDrawer is not true
    if (!preventCartDrawer && this.hasAttribute('auto-open')) {
      //console.log('Opening cart drawer');
      this.showDialog();
    } else if (preventCartDrawer) {
      //console.log('Cart drawer opening prevented by flag');
    }
    
    // Note: You can still handle other cart update logic here if needed
    // For example, updating cart contents, item counts, etc.
  };

  open() {
    console.log('Cart drawer open() method called - checking call stack:', new Error().stack);
    this.showDialog();

    // Dispatch a custom event to notify other components that the cart drawer is open
    this.dispatchEvent(new CustomEvent('cart-drawer:opened', { 
      bubbles: true,
      detail: { timestamp: Date.now() }
    }));

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  /**
   * Updates the cart contents with new HTML from sections
   * @param {Object} sections - The sections data from the cart response
   */
  #updateCartContents(sections) {
    try {
      // Find the cart-items-component within this cart drawer
      const cartItemsComponent = this.querySelector('cart-items-component');
      if (cartItemsComponent && cartItemsComponent.sectionId) {
        const sectionId = cartItemsComponent.sectionId;
        const newHtml = sections[sectionId];
        
        if (newHtml) {
          console.log('Updating cart contents for section:', sectionId);
          // Import morphSection dynamically to avoid circular dependencies
          import('@theme/section-renderer').then(({ morphSection }) => {
            morphSection(sectionId, newHtml);
          }).catch(error => {
            console.error('Error importing morphSection:', error);
          });
        } else {
          console.log('No HTML found for section:', sectionId);
        }
      } else {
        console.log('Cart items component not found or missing section ID');
      }
    } catch (error) {
      console.error('Error updating cart contents:', error);
    }
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
